import { useState, useMemo } from "react";
import { num, toCm, m2, money, ceilStep, minSale, svPrice, CalcLayout, FormCol, Card, Field, Sel, HowTo, Advanced, ResultCard } from "./shared";

export default function Persianas({ onSave, s }) {
  const [u, setU] = useState("m");
  const [tipo, setTipo] = useState("Roller");
  const [instal, setInstal] = useState("Dentro del marco");
  const [ancho, setAncho] = useState("1.20");
  const [alto, setAlto] = useState("1.80");
  const [control, setControl] = useState("Cadena");
  const [ladoControl, setLado] = useState("Derecho");
  const [accion, setAccion] = useState("Manual");
  const [domotica, setDomotica] = useState("Sin integración");
  const [cantidad, setCantidad] = useState("1");
  const [ventaMin, setVentaMin] = useState("1");
  const [redondeo, setRedondeo] = useState("0.01");
  const [precioM2, setPrecioM2] = useState(svPrice(s, "precio_persiana_m2"));
  const [precioInst, setPrecioInst] = useState(svPrice(s, "precio_instalacion"));
  const [precioMotor, setPrecioMotor] = useState("");
  const motorizada = tipo === "Motorizada" || control === "Motorizado" || accion !== "Manual";

  const r = useMemo(() => {
    const cant = Math.max(1, Math.round(num(cantidad)) || 1);
    const anchoCm = toCm(ancho, u), altoCm = toCm(alto, u);
    const area = (anchoCm / 100) * (altoCm / 100);
    let cobrable = area < 1 ? 1 : ceilStep(area, num(redondeo));
    cobrable = minSale(cobrable, num(ventaMin));
    const totalM2 = cobrable * cant;
    const altoM = altoCm / 100, anchoM = anchoCm / 100;
    const sop = altoM <= 1.5 ? 2 : altoM <= 2.5 ? 3 : 3 + Math.ceil(altoM - 2.5);
    const tubo = anchoM <= 1.5 ? "Ø 32 mm" : anchoM <= 2.5 ? "Ø 38 mm" : "Ø 45 mm";
    const cMat = totalM2 * num(precioM2);
    const cMotor = motorizada ? num(precioMotor) : 0;
    const cInst = num(precioInst);
    const total = cMat + cMotor + cInst;
    const w = [];
    if (instal === "Dentro del marco") w.push("Mide ancho (arriba/centro/abajo) y alto (izq/centro/der); usa la medida MENOR. Verifica profundidad del marco.");
    if (area > 0 && area < 1) w.push("El área es menor a 1 m²: se aplica el mínimo de fabricación y cobro de 1.00 m².");
    if (anchoM > 3) w.push("Ancho grande: puede requerir dividir en dos módulos o refuerzo de tubo.");
    if (motorizada && domotica === "Sin integración") w.push("Persiana motorizada sin integración Smart Home seleccionada.");
    if (altoM > 3) w.push("Altura elevada: valida el torque del motor y el peso de la tela.");
    const rows = [
      { section: "Persiana" },
      { label: "Tipo de persiana", value: tipo }, { label: "Instalación", value: instal },
      { label: "Ancho", value: `${anchoM.toFixed(2)} m` }, { label: "Alto", value: `${altoM.toFixed(2)} m` },
      { label: "Control", value: `${control} · ${ladoControl}` }, { label: "Accionamiento", value: accion },
      ...(motorizada ? [{ label: "Domótica", value: domotica }] : []),
      { label: "Cantidad", value: `${cant}` },
      { section: "Cálculo" },
      { label: "Área real", value: m2(area) },
      { label: "Área cobrable (unit.)", value: m2(cobrable) },
      { label: "Total m² cobrables", value: m2(totalM2), strong: true },
      { section: "Componentes" },
      { label: "Tubo sugerido", value: tubo }, { label: "Soportes", value: `${sop * cant}` },
      ...(motorizada ? [{ label: "Motorización", value: "Motor + fuente + control + receptor" }] : []),
      { section: "Costos" },
      { label: "Materiales (m² × precio)", value: money(cMat) },
      ...(motorizada ? [{ label: "Motorización", value: money(cMotor) }] : []),
      { label: "Instalación", value: money(cInst) },
      { label: "Total estimado", value: money(total), strong: true },
    ];
    const inputs = { u, tipo, ancho, alto, cantidad, control, accion };
    return { rows, warnings: w, total, inputs };
  }, [u, tipo, instal, ancho, alto, control, ladoControl, accion, domotica, cantidad, ventaMin, redondeo, precioM2, precioInst, precioMotor, motorizada]);

  return (
    <CalcLayout>
      <FormCol>
        <HowTo title="¿Cómo medir una persiana?">
          <p><b>Dentro del marco:</b> mide ancho (arriba/centro/abajo) y alto (izq/centro/der) → usa la medida menor. <b>Fuera:</b> +5–10 cm por lado y +10–20 cm arriba.</p>
        </HowTo>
        <Card>
          <Sel label="Unidad de medida" value={u} onChange={setU} opts={[["mm", "Milímetros"], ["cm", "Centímetros"], ["m", "Metros"]]} testid="pers-unidad" />
          <Sel label="Tipo de persiana" value={tipo} onChange={setTipo} opts={["Roller", "Blackout", "Screen", "Zebra", "Sheer Elegance", "Romana", "Panel Japonés", "Vertical PVC", "Vertical Tela", "Horizontal Aluminio", "Horizontal Madera", "Horizontal PVC", "Celular (Honeycomb)", "Shangri-La", "Silhouette", "Motorizada"]} testid="pers-tipo" />
          <Field label="Ancho" value={ancho} onChange={setAncho} suffix={u} testid="pers-ancho" />
          <Field label="Alto" value={alto} onChange={setAlto} suffix={u} testid="pers-alto" />
          <Sel label="Instalación" value={instal} onChange={setInstal} opts={["Dentro del marco", "Fuera del marco", "Techo", "Muro", "Personalizada"]} testid="pers-instal" />
          <Sel label="Control" value={control} onChange={setControl} opts={["Cadena", "Cordón", "Bastón", "Motorizado"]} testid="pers-control" />
          <Sel label="Lado del control" value={ladoControl} onChange={setLado} opts={["Izquierdo", "Derecho"]} testid="pers-lado" />
          <Sel label="Accionamiento" value={accion} onChange={setAccion} opts={["Manual", "Motorizado", "Smart Home"]} testid="pers-accion" />
          <Sel label="Sistema domótico" value={domotica} onChange={setDomotica} opts={["Sin integración", "Google Home", "Alexa", "Apple HomeKit", "Matter", "Zigbee", "Bluetooth", "WiFi"]} testid="pers-domotica" />
          <Field label="Cantidad de persianas iguales" value={cantidad} onChange={setCantidad} step="1" min="1" testid="pers-cantidad" />
          <Sel label="Venta mínima (m²)" value={ventaMin} onChange={setVentaMin} opts={[["0.75", "0.75 m²"], ["1", "1.00 m²"], ["1.20", "1.20 m²"]]} testid="pers-ventamin" />
          <Sel label="Redondeo" value={redondeo} onChange={setRedondeo} opts={[["0.01", "Centésima (exacto)"], ["0.1", "Décima"], ["0.25", "Al siguiente cuarto"], ["0.5", "Al siguiente medio m²"]]} testid="pers-redondeo" />
        </Card>
        <Advanced>
          <Field label="Precio ($/m²)" value={precioM2} onChange={setPrecioM2} suffix="$" testid="pers-precio-m2" />
          <Field label="Motorización ($)" value={precioMotor} onChange={setPrecioMotor} suffix="$" testid="pers-precio-motor" />
          <Field label="Instalación ($)" value={precioInst} onChange={setPrecioInst} suffix="$" testid="pers-precio-inst" />
        </Advanced>
      </FormCol>
      <ResultCard title="Cotización de Persiana — GOÛT" modality="persianas" rows={r.rows} warnings={r.warnings} total={r.total} inputs={r.inputs} onSave={onSave} />
    </CalcLayout>
  );
}
